import { useMutation } from '@tanstack/react-query';
import type { CreatePat, PatCreated } from '@salonomy/types';
import { api, expectOk, unwrap } from '../lib/api.js';

/**
 * Personal access tokens for the public API (WS6 developer panel). ADR-0006: the raw token is
 * returned ONCE at creation (only a hash is stored), so there is deliberately NO list endpoint —
 * `POST /tokens` (mint) and `DELETE /tokens/:id` (revoke) are the entire surface. The panel keeps
 * the tokens it minted in component state so it can show "copy it now" once and offer revoke after,
 * but it can never re-list tokens minted in a previous session. Owner/manager only (mirrors the API
 * requireManager guard); the UI gates the whole panel before the server ever sees a 403.
 *
 * No query cache to invalidate (nothing is listed); these are plain mutations.
 */

export function useCreateToken() {
  return useMutation({
    mutationFn: async (body: CreatePat): Promise<PatCreated> =>
      // CreatePat over-requires `scopes` (it has a default); cast at the boundary — the server
      // re-validates against the same Zod schema.
      unwrap(await api.POST('/v1/auth/tokens', { body: body as never })),
  });
}

export function useRevokeToken() {
  return useMutation({
    mutationFn: async (id: string): Promise<void> =>
      expectOk(await api.DELETE('/v1/auth/tokens/{id}', { params: { path: { id } } })),
  });
}
